import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useState } from 'react';
import { TenkeyOpenEventType, TenkeyType } from 'types/support/supportType';
import Tenkey, { INITIAL_TEN_KEY } from 'components/support/organisms/supports/Tenkey';
import TenkeyInput from 'components/support/atoms/inputs/TenkeyInput';
import { ledSelectAreaDuoType } from 'types/machineConf/ledSelectAreaType';
import COMMON from 'constants/common';

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  rgbSettingArea: {
    position: 'relative',
    width: 582,
    height: 133,
    backgroundColor: '#transparent',
    border: '3px solid #8397CD',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  rgbInputArea: {
    width: '163px',
    height: '46px',
    border: '3px solid #8397CD',
    borderRadius: '10px',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    overflow: 'hidden',
  },
  inputTenType: {
    height: '40px',
    width: '122px',
    backgroundColor: '#4D4D4D',
    border: '1px solid #4D4D4D',
    borderRadius: '0 4px 4px 0',
    color: '#fff',
    fontSize: 23,
    '& input': {
      textAlign: 'center',
    },
  },
  whiteCover: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '90%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)' /* 半透明白 */,
    zIndex: 100,
  },
}));

/* ******** Constants ******** */
const TENKEY_OPTION = {
  COLOR: {
    mode: COMMON.TENKEY_MODE.INTEGER,
    integerSize: 3,
    scaleSize: 0,
    maxValue: 255,
    minValue: 0,
  },
};

/* ******** Main Component ******** */
/**
 *
 * @Params leftRight, ledSelectAreaConf, onChange
 * @returns {React.FC} - LED 設定エリア
 *
 */
const LedSelectAreaDuo: React.FC<React.PropsWithChildren<ledSelectAreaDuoType>> = React.memo((props) => {
  const { ledSelectAreaConf, onChange } = props;
  const { classes } = useStyles();
  // Local State
  const [tenkey, setTenkey] = React.useState(INITIAL_TEN_KEY.tenkey);
  const [activePane, setActivePane] = useState<Pane>('main');

  /* ******** Events ******** */
  const handleDecisionTenkey = useCallback(
    (value: string, name: string) => {
      onChange({ ...ledSelectAreaConf, [name]: Number(value) });
      setTenkey(INITIAL_TEN_KEY.tenkey);
    },
    [ledSelectAreaConf],
  );

  const handleCloseTenkey = useCallback(() => {
    setTenkey(INITIAL_TEN_KEY.tenkey);
  }, []);

  const initialTenkey: TenkeyType = {
    ...INITIAL_TEN_KEY.tenkey,
    open: true,
    onDecision: handleDecisionTenkey,
    onClose: handleCloseTenkey,
  };
  // サブカラーホワイトアウト
  // tenkey input
  const handleOpenMainColorR: TenkeyOpenEventType = useCallback(() => {
    setTenkey({
      ...initialTenkey,
      anchorPosition: { top: 100, left: 284 },
      name: 'mainR',
      option: TENKEY_OPTION.COLOR,
      value: String(ledSelectAreaConf.mainR) || '0',
    });
  }, [ledSelectAreaConf]);

  const handleOpenMainColorG: TenkeyOpenEventType = useCallback(() => {
    setTenkey({
      ...initialTenkey,
      anchorPosition: { top: 100, left: 454 },
      name: 'mainG',
      option: TENKEY_OPTION.COLOR,
      value: String(ledSelectAreaConf.mainG) || '0',
    });
  }, [ledSelectAreaConf]);

  const handleOpenMainColorB: TenkeyOpenEventType = useCallback(() => {
    setTenkey({
      ...initialTenkey,
      anchorPosition: { top: 100, left: 638 },
      name: 'mainB',
      option: TENKEY_OPTION.COLOR,
      value: String(ledSelectAreaConf.mainB) || '0',
    });
  }, [ledSelectAreaConf]);

  const handleOpenSubColorR: TenkeyOpenEventType = useCallback(() => {
    setTenkey({
      ...initialTenkey,
      anchorPosition: { top: 100, left: 284 },
      name: 'subR',
      option: TENKEY_OPTION.COLOR,
      value: String(ledSelectAreaConf.subR) || '0',
    });
  }, [ledSelectAreaConf]);

  const handleOpenSubColorG: TenkeyOpenEventType = useCallback(() => {
    setTenkey({
      ...initialTenkey,
      anchorPosition: { top: 100, left: 454 },
      name: 'subG',
      option: TENKEY_OPTION.COLOR,
      value: String(ledSelectAreaConf.subG) || '0',
    });
  }, [ledSelectAreaConf]);

  const handleOpenSubColorB: TenkeyOpenEventType = useCallback(() => {
    setTenkey({
      ...initialTenkey,
      anchorPosition: { top: 100, left: 638 },
      name: 'subB',
      option: TENKEY_OPTION.COLOR,
      value: String(ledSelectAreaConf.subB) || '0',
    });
  }, [ledSelectAreaConf]);

  type Pane = 'main' | 'sub';
  const toggleCover = useCallback(() => {
    setActivePane((prev) => (prev === 'main' ? 'sub' : 'main'));
  }, []);

  /* ************ Params ************ */

  return (
    <>
      <Box
        sx={{
          width: 800,
          height: 466,
          display: 'grid',
          justifyContent: 'start',
          alignContent: 'center',
          gap: '10px',
          margin: '0 auto',
        }}>
        {/* メインカラー・サブカラー */}
        <Box
          sx={{
            height: 282,
            display: 'block',
            justifyContent: 'center',
            paddingLeft: '110px',
            boxSizing: 'border-box',
          }}>
          {/* メインカラー */}
          <Box className={classes.rgbSettingArea}>
            <Box
              sx={{
                height: 42,
                borderBottom: '3px solid #8397CD',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: '16px',
              }}>
              <Box sx={{ width: '208px', height: '100%', padding: '10px 0 10px 32px', backgroundColor: '#8397CD' }}>
                メインカラー
              </Box>
              <Box
                sx={{
                  width: '374px',
                  height: '100%',
                  borderRadius: '0 4px 0 0',
                  backgroundColor: `rgb(${ledSelectAreaConf.mainR}, ${ledSelectAreaConf.mainG}, ${ledSelectAreaConf.mainB})`,
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', height: 91, padding: '22px', gap: '17px', position: 'relative' }}>
              <Box className={classes.rgbInputArea}>
                <Box sx={{ width: '25%', height: '100%', padding: '10px 5px', backgroundColor: '#8397CD' }}>R</Box>
                <TenkeyInput
                  className={classes.inputTenType}
                  onOpen={handleOpenMainColorR}
                  isSelected={tenkey.name === 'mainR'}
                  value={ledSelectAreaConf.mainR}
                />
              </Box>
              <Box className={classes.rgbInputArea}>
                <Box sx={{ width: '25%', height: '100%', padding: '10px 5px', backgroundColor: '#8397CD' }}>G</Box>
                <TenkeyInput
                  className={classes.inputTenType}
                  onOpen={handleOpenMainColorG}
                  isSelected={tenkey.name === 'mainG'}
                  value={ledSelectAreaConf.mainG}
                />
              </Box>
              <Box className={classes.rgbInputArea}>
                <Box sx={{ width: '25%', height: '100%', padding: '10px 5px', backgroundColor: '#8397CD' }}>B</Box>
                <TenkeyInput
                  className={classes.inputTenType}
                  onOpen={handleOpenMainColorB}
                  isSelected={tenkey.name === 'mainB'}
                  value={ledSelectAreaConf.mainB}
                />
              </Box>
              {activePane === 'sub' && <Box className={classes.whiteCover} onClick={toggleCover} />}
            </Box>
          </Box>

          {/* サブカラー */}
          <Box className={classes.rgbSettingArea}>
            <Box
              sx={{
                height: 42,
                borderBottom: '3px solid #8397CD',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: '16px',
              }}>
              <Box sx={{ width: '208px', height: '100%', padding: '10px 0 10px 32px', backgroundColor: '#8397CD' }}>
                サブカラー
              </Box>
              <Box
                sx={{
                  width: '374px',
                  height: '100%',
                  borderRadius: '0 4px 0 0',
                  backgroundColor: `rgb(${ledSelectAreaConf.subR}, ${ledSelectAreaConf.subG}, ${ledSelectAreaConf.subB})`,
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', height: 91, padding: '22px', gap: '17px', position: 'relative' }}>
              <Box className={classes.rgbInputArea}>
                <Box sx={{ width: '25%', height: '100%', padding: '10px 5px', backgroundColor: '#8397CD' }}>R</Box>
                <TenkeyInput
                  className={classes.inputTenType}
                  onOpen={handleOpenSubColorR}
                  isSelected={tenkey.name === 'subR'}
                  value={ledSelectAreaConf.subR}
                />
              </Box>
              <Box className={classes.rgbInputArea}>
                <Box sx={{ width: '25%', height: '100%', padding: '10px 5px', backgroundColor: '#8397CD' }}>G</Box>
                <TenkeyInput
                  className={classes.inputTenType}
                  onOpen={handleOpenSubColorG}
                  isSelected={tenkey.name === 'subG'}
                  value={ledSelectAreaConf.subG}
                />
              </Box>
              <Box className={classes.rgbInputArea}>
                <Box sx={{ width: '25%', height: '100%', padding: '10px 5px', backgroundColor: '#8397CD' }}>B</Box>
                <TenkeyInput
                  className={classes.inputTenType}
                  onOpen={handleOpenSubColorB}
                  isSelected={tenkey.name === 'subB'}
                  value={ledSelectAreaConf.subB}
                />
              </Box>
              {activePane === 'main' && <Box className={classes.whiteCover} onClick={toggleCover} />}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            height: 138,
            width: 800,
            display: 'block',
            justifyContent: 'start',
            gap: '16px',
            padding: '34px 0 0 34px',
            boxSizing: 'border-box',
            fontSize: '20px',
          }}>
          <Box
            sx={{
              height: 58,
            }}>
            <div>※クレナグランデュオのカスタム設定は、他機種とのグループ設定できない、</div>
            <div style={{ paddingLeft: '10px' }}>クレナグランデュオ同士のみグループ設定可能な設定となります。</div>
          </Box>
          <Box
            sx={{
              height: 58,
              color: '#ff0000',
            }}>
            <div>※かんたん設定の演出内容㉒レッド～㉖ホワイトは、</div>
            <div style={{ paddingLeft: '10px' }}>クレナグランデュオとクレナマルチ用の演出となります。</div>
          </Box>
        </Box>
      </Box>
      <Tenkey tenkey={tenkey} />
    </>
  );
});

export default LedSelectAreaDuo;
